/**
 * Utility per il preloading dei dati più comuni
 * per migliorare la performance percepita dall'utente
 */

import { fetchPromos, fetchLatestPromos } from '@/services/teablePromoService';
import { getTableRecords } from '@/services/teableApi';
import { getTeableConfig, isTeableConfigured } from '@/config/teableConfig';
import { cacheService } from '@/services/cacheService';
import { secureLog } from '@/utils/secureLogger';

let isPreloading = false;

/**
 * Precarica i dati più comuni in background
 */
export const preloadCommonData = async (): Promise<void> => {
  // Evita preload multipli simultanei
  if (isPreloading) {
    return;
  }

  isPreloading = true;

  try {
    // Precarica in parallelo per massima velocità
    await Promise.all([
      fetchPromos(),           // Tutte le promozioni
      fetchLatestPromos(),     // Ultime 3 promozioni
      prefetchLatestInsertions(), // Ultimi 10 inserimenti per footer
    ]);
  } catch (error) {
    secureLog.error('Preload error:', error);
  } finally {
    isPreloading = false;
  }
};

/**
 * Prefetch degli ultimi inserimenti per il footer
 */
const prefetchLatestInsertions = async (): Promise<void> => {
  if (!isTeableConfigured()) return;
  
  const cached = cacheService.get('latest_insertions');
  if (cached) return; // Già in cache
  
  try {
    const config = getTeableConfig();
    const records = await getTableRecords(config.promoTableId, {
      sort: [{ field: 'DataCreazione', order: 'desc' }],
      maxRecords: 10
    });
    cacheService.set('latest_insertions_raw', records, 2 * 60 * 1000);
    secureLog.log(`✅ Prefetched ${records.length} latest insertions`);
  } catch (error) {
    secureLog.error('Prefetch latest insertions error:', error);
  }
};

/**
 * Avvia il preload con un piccolo delay per non bloccare il caricamento iniziale
 */
export const initPreload = (): void => {
  // Avvia dopo che il rendering iniziale è completato per non impattare LCP
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => {
      preloadCommonData();
    }, { timeout: 3000 });
  } else {
    setTimeout(() => {
      preloadCommonData();
    }, 2000);
  }
};
